import React, { forwardRef, ReactNode, useContext, useEffect } from 'react';
import clsx from 'clsx';
import {
  Checkbox as RACCheckbox,
  CheckboxProps as AriaCheckboxProps,
  CheckboxGroupStateContext,
} from 'react-aria-components';
import { useDOMRef } from '@react-spectrum/utils';
import { FocusableRef, forwardRefType } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/checkbox/checkbox.module.css';
import intlMessages from '../../Label/i18n';
import { useMessageFormatter } from '@react-aria/i18n';

import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { useCheckboxGroupContext } from '../../CheckboxGroup/src/CheckboxGroupContext';
import { useThemeCheck } from '../../utils/useThemeCheck';
import {
  AriaTogglePropsKeys,
  AriaValidationPropsKeys,
  BaseComponentKeys,
  FocusablePropsKeys,
  GlobalDomAttributes,
  InputBaseKeys,
} from '../../utils/types';
import { filterDOMProps } from 'react-aria/filterDOMProps';

const indeterminateIcon = (
  <svg
    className={clsx(styles['sapphire-checkbox__box-icon'])}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    role="img"
    aria-hidden
  >
    <g fill="none" fillRule="evenodd">
      <path
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
        d="M7 12h10"
      />
      <path fill="currentColor" fillRule="nonzero" d="M18 11v2H6v-2z" />
    </g>
  </svg>
);

const checkedIcon = (
  <svg
    className={clsx(styles['sapphire-checkbox__box-icon'])}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    role="img"
    aria-hidden
  >
    <path
      d="M17.782 6.918l.753.659a.5.5 0 01.047.705L10.9 17.061a.5.5 0 01-.73.024l-4.232-4.231a.5.5 0 010-.708l.707-.707a.5.5 0 01.708 0l3.097 3.097 6.626-7.57a.5.5 0 01.705-.048z"
      fill="currentColor"
      fillRule="nonzero"
    />
  </svg>
);

export type CheckboxProps = Pick<
  AriaCheckboxProps,
  | 'children'
  | 'isIndeterminate'
  | AriaTogglePropsKeys
  | AriaValidationPropsKeys
  | 'id'
  | FocusablePropsKeys
  | InputBaseKeys
  | 'name'
  | BaseComponentKeys
> &
  SapphireStyleProps &
  GlobalDomAttributes & {
    /**
     * Whether user input is required on the input before form submission
     */
    isRequired?: boolean;
    /**
     * To visually indicate if this field is required or optional.
     * @default false
     */
    necessityIndicator?: boolean;
    /**
     * If set, the checkbox becomes invalid, with or without an error message.
     */
    error?: ReactNode;
    /**
     * If set, the checkbox displays note text below the main label.
     */
    note?: ReactNode;
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';

    /**
     * Internal slot prop to allow for composition with RAC components.
     * We might decide to offer the same composition API for Sapphire components in the future,
     * but until that's a clear direction, we will keep this internal.
     */
    INTERNAL_slot?: AriaCheckboxProps['slot'];
  };

export const Checkbox = (forwardRef as forwardRefType)(function Checkbox(
  {
    necessityIndicator = false,
    children,
    size: sizeProp,
    error,
    note,
    ...props
  }: CheckboxProps,
  ref: FocusableRef<HTMLLabelElement>
) {
  useThemeCheck();

  const { isDisabled: isInDisabledGroup } =
    useContext(CheckboxGroupStateContext) ?? {};
  const groupContext = useCheckboxGroupContext();
  const size = groupContext?.size || sizeProp || 'lg';
  const formatMessage = useMessageFormatter(intlMessages);

  const { styleProps } = useSapphireStyleProps(props);

  const indicator =
    props.isRequired && necessityIndicator
      ? 'required'
      : !props.isRequired && necessityIndicator
      ? 'optional'
      : undefined;

  useEffect(() => {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV !== 'production' &&
      sizeProp &&
      groupContext?.size &&
      sizeProp !== groupContext?.size
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `size "${groupContext.size}" prop set on the CheckboxGroup overrides the child Checkbox's size: "${sizeProp}"`
      );
    }
  }, [sizeProp, groupContext?.size]);

  // TODO(v6): move style props to the wrapping <div>
  return (
    <div
      className={styles['sapphire-checkbox-container']}
      {...filterDOMProps(props, { global: true })}
    >
      <RACCheckbox
        {...props}
        slot={props.INTERNAL_slot}
        isInvalid={Boolean(error)}
        ref={useDOMRef(ref)} // TODO(v6): replace with normal ref
        className={({
          isDisabled,
          isSelected,
          isIndeterminate,
          isHovered,
          isFocusVisible,
          isPressed,
        }) =>
          clsx(
            styleProps.className,
            styles['sapphire-checkbox'],
            styles['js-hover'],
            styles['js-focus'],
            {
              [styles['sapphire-checkbox--indeterminate']]: isIndeterminate,
              [styles['sapphire-checkbox--error']]: error,
              [styles['sapphire-checkbox--md']]: size === 'md',
              // disabled style is handled by opacity, so shouldn't be added again, if already
              // getting it from the field UI.
              [styles['is-disabled']]: isDisabled && !isInDisabledGroup,
              [styles['is-hover']]: isHovered,
              [styles['is-focus']]: isFocusVisible,
              [styles['is-active']]: isPressed,
              [styles['sapphire-checkbox--checked']]: isSelected,
            }
          )
        }
        style={styleProps.style}
      >
        {({ isIndeterminate, isSelected }) => (
          <>
            <span className={clsx(styles['sapphire-checkbox__box'])}>
              {isIndeterminate
                ? indeterminateIcon
                : isSelected
                ? checkedIcon
                : null}
            </span>
            <span className={clsx(styles['sapphire-checkbox__label'])}>
              {children}
              {indicator === 'required' ? (
                <span
                  className={clsx(
                    styles['sapphire-checkbox__required-indicator']
                  )}
                ></span>
              ) : indicator === 'optional' ? (
                <span
                  className={clsx(
                    styles['sapphire-checkbox__optional-indicator']
                  )}
                >
                  ({formatMessage('optional')})
                </span>
              ) : null}
            </span>
          </>
        )}
      </RACCheckbox>
      {typeof error === 'string' && (
        <div
          className={clsx(styles['sapphire-checkbox__error-text'], {
            [styles['sapphire-checkbox__error-text--md']]: size === 'md',
          })}
        >
          {error}
        </div>
      )}
      {typeof note === 'string' && (
        <div
          className={clsx(styles['sapphire-checkbox__note'], {
            [styles['sapphire-checkbox__note--md']]: size === 'md',
          })}
        >
          {note}
        </div>
      )}
    </div>
  );
});
